import { createElement, useEffect, useState, type ComponentType, type ReactNode } from 'react'
import type * as PaymentModalModule from './PaymentModal'
import type { CardEntryPaymentState, StripeConfiguration } from './PaymentForm'

type PaymentModalLoadStatus = 'not_started' | 'loading' | 'loaded' | 'failed'

/** Where loading Pay Now's code stands, and how long it has taken (or took). */
export interface PaymentModalLoadState {
  status: PaymentModalLoadStatus
  elapsedMs: number
}

export function createPaymentModalModuleLoader<Module>(importModule: () => Promise<Module>) {
  let module: Module | null = null
  let loadPromise: Promise<Module> | null = null
  let loadStartedAt: number | null = null
  let loadFinishedAt: number | null = null
  let loadStatus: PaymentModalLoadStatus = 'not_started'

  const load = (): Promise<Module> => {
    if (module) {
      return Promise.resolve(module)
    }

    if (loadPromise) {
      return loadPromise
    }

    loadStatus = 'loading'
    loadStartedAt = Date.now()
    loadFinishedAt = null

    loadPromise = importModule()
      .then((loadedModule) => {
        module = loadedModule
        loadStatus = 'loaded'
        loadFinishedAt = Date.now()

        return loadedModule
      })
      .catch((error: unknown) => {
        loadStatus = 'failed'
        loadFinishedAt = Date.now()
        loadPromise = null

        throw error
      })

    return loadPromise
  }

  return {
    getModule: (): Module | null => module,
    /** Resolves once the module has loaded or failed, or after `ms` at most, whichever comes first. */
    waitFor: (ms: number): Promise<void> =>
      Promise.race([
        load().then(
          () => {},
          () => {}
        ),
        new Promise<void>((resolve) => globalThis.setTimeout(resolve, ms)),
      ]),
    getState: (): PaymentModalLoadState => {
      const finishedAt = loadFinishedAt ?? Date.now()

      return {
        status: loadStatus,
        elapsedMs: loadStartedAt === null ? 0 : Math.max(0, finishedAt - loadStartedAt),
      }
    },
    load,
  }
}

const paymentModalModuleLoader = createPaymentModalModuleLoader(() => import('./PaymentModal'))

/** How long opening Pay Now waits for its code before showing the loading stand-in. */
const PAYMENT_MODAL_CODE_WAIT_MS = 3_000

/**
 * Waits for Pay Now's code before Pay Now opens. Opening before it arrives shows a stand-in dialog that the real one
 * replaces, which reads as Pay Now blinking. A few seconds at most: a slow or stalled load opens on the stand-in, which
 * shows it loading and offers Cancel, and Retry if it fails.
 */
export function waitForPaymentModalCode(): Promise<void> {
  return paymentModalModuleLoader.waitFor(PAYMENT_MODAL_CODE_WAIT_MS)
}

/** Loads Pay Now's code, and Stripe for the payment state's card entry, before Pay Now opens. */
export async function preloadPaymentModal({
  paymentState = null,
  paymentBootstrap = null,
}: {
  paymentState?: CardEntryPaymentState | null
  paymentBootstrap?: StripeConfiguration | null
} = {}): Promise<typeof PaymentModalModule> {
  const module = await paymentModalModuleLoader.load()

  if (paymentState) {
    await module.preloadPaymentResources({ paymentState, paymentBootstrap })
  }

  return module
}

export function getPaymentModalLoadState(): PaymentModalLoadState {
  return paymentModalModuleLoader.getState()
}

/** What the page shows until Pay Now's code loads, given the load's error (null while loading) and a retry. */
type PaymentModalFallback = ReactNode | ((load: { error: unknown; retry: () => void }) => ReactNode)

interface LazyPaymentModalProps extends PaymentModalModule.PaymentModalProps {
  fallback?: PaymentModalFallback
}

export function PaymentModal({ fallback = null, ...props }: LazyPaymentModalProps) {
  const [PaymentModalComponent, setPaymentModalComponent] =
    useState<ComponentType<PaymentModalModule.PaymentModalProps> | null>(
      () => paymentModalModuleLoader.getModule()?.PaymentModal ?? null
    )
  const [loadError, setLoadError] = useState<unknown>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    if (PaymentModalComponent) {
      return
    }

    let isCurrentAttempt = true

    paymentModalModuleLoader
      .load()
      .then((module) => {
        if (isCurrentAttempt) {
          setPaymentModalComponent(() => module.PaymentModal)
        }
      })
      .catch((error: unknown) => {
        if (isCurrentAttempt) {
          setLoadError(error)
        }
      })

    return () => {
      isCurrentAttempt = false
    }
  }, [PaymentModalComponent, loadAttempt])

  if (PaymentModalComponent) {
    return createElement(PaymentModalComponent, props)
  }

  return typeof fallback === 'function'
    ? fallback({
        error: loadError,
        retry: () => {
          setLoadError(null)
          setLoadAttempt((attempt) => attempt + 1)
        },
      })
    : fallback
}
